import { CalendarBlank, Clock, MapPin, PencilSimple, ShieldCheck } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { formatEventDate, formatRelative } from "@/lib/dates";
import { AddToCalendarMenu } from "./AddToCalendarMenu";
import styles from "./EventHero.module.css";

export type EventHeroData = {
  shortcut: string;
  name: string;
  description: string | null;
  location: string | null;
  eventAt: string;
  timezone: string;
  deadline: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
};

type Props = {
  event: EventHeroData;
  /** "admin" zeigt Admin-Chip + optionalen Edit-Trigger; "user" zeigt User-Chip; "guest" keine Chips. */
  role: "admin" | "user" | "guest";
  /** Nur bei admin: Edit-Modus aktivieren. */
  onEdit?: () => void;
  /** Nur bei admin: PRO/Free-Tier-Chip. */
  tier?: "free" | "pro";
  /** Public Gast-Link — wird im Kalender-Eintrag eingebettet. */
  guestUrl: string;
};

export function EventHero({ event, role, onEdit, tier, guestUrl }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "de";
  const canceled = event.canceledAt !== null;

  return (
    <header className={styles.hero}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{event.name}</h1>
        {role === "admin" && onEdit && !canceled && (
          <Button type="button" variant="secondary" size="compact" onClick={onEdit}>
            <PencilSimple size={16} weight="regular" />
            {t("admin.editDetails")}
          </Button>
        )}
      </div>

      <div className={styles.meta}>
        <span className={styles.metaItem}>
          <CalendarBlank size={16} weight="regular" />
          {formatEventDate(event.eventAt, event.timezone, locale)}
        </span>
        <span className={`${styles.metaItem} ${styles.metaMuted}`}>
          {formatRelative(event.eventAt, locale)}
        </span>
        {event.location && (
          <span className={styles.metaItem}>
            <MapPin size={16} weight="regular" />
            {event.location}
          </span>
        )}
        {event.deadline && (
          <span className={`${styles.metaItem} ${styles.metaMuted}`}>
            <Clock size={14} weight="regular" />
            {t("admin.deadlineShort", { date: formatEventDate(event.deadline, event.timezone, locale) })}
          </span>
        )}
        {!canceled && (
          <AddToCalendarMenu
            shortcut={event.shortcut}
            title={event.name}
            startIso={event.eventAt}
            location={event.location}
            description={event.description}
            guestUrl={guestUrl}
          />
        )}
      </div>

      {(role !== "guest" || tier) && (
        <div className={styles.chipsRow}>
          {tier && (
            <span className={`${styles.chip} ${tier === "pro" ? styles.chipPro : ""}`}>
              {tier === "pro" ? t("event.tierPro") : t("event.tierFree")}
            </span>
          )}
          {role === "admin" && (
            <span className={styles.chip}>
              <ShieldCheck size={14} weight="regular" />
              {t("event.roleAdmin")}
            </span>
          )}
          {role === "user" && (
            <span className={styles.chip}>
              {t("event.roleUser")}
            </span>
          )}
        </div>
      )}

      {event.description && <p className={styles.description}>{event.description}</p>}
    </header>
  );
}
